<template>
    <div class="c-row">
        <label class="c-row-title">
            {{data.title}}
            <span
                tabindex="-1"
                v-if="data.info"
                class="c-row-title__info-icon"
            >&#9432;</span>
            <span v-if="data.subtitle">{{data.subtitle}}</span>
            <div class="c-row-title__info">
                <p>{{data.info}}</p>
            </div>
        </label>

        <field
            text="Purchase/Spend Frequency"
            :sanitizeFunction="sanitizeIntegerValue"
            @changed="setFrequency"
            :readonly="false"
            :initialValue="spendFrequency"
        />

        <field-select text="Per Selected Period" @changed="setPeriod" :initialValue="period"/>

        <field
            text="Amount($) per Occasion"
            :sanitizeFunction="sanitizeCurrencyValue"
            @changed="setAmount"
            :readonly="false"
            :isCurrency="true"
            :initialValue="amount"
        />
    </div>
</template>

<script>
    import Field from "components/field/Field.vue";
    import Select from "components/select/Select.vue";
    import { mapActions, mapGetters } from "vuex";
    import { formatCurrency } from "utils";

    export default {
        components: {
            field: Field,
            "field-select": Select
        },

        props: {
            data: {
                type: Object,
                required: true
            }
        },

        data() {
            return {
                total: 0,
                period: 52,
                amount: undefined,
                spendFrequency: undefined
            };
        },

        computed: {
            ...mapGetters(["steps"])
        },

        methods: {
            ...mapActions(["updateRow"]),

            setPeriod(period) {
                this.period = period;
                this.calculateTotal();
            },

            setFrequency(frequency) {
                this.spendFrequency = frequency;
                this.calculateTotal();
            },

            getFrequency() {
                if (
                    this.steps[this.data.stepIndex].rows[this.data.id] &&
                    this.steps[this.data.stepIndex].rows[this.data.id]
                        .spendFrequency
                ) {
                    this.spendFrequency = this.steps[this.data.stepIndex].rows[
                        this.data.id
                    ].spendFrequency;
                }
            },

            setAmount(amount) {
                this.amount = amount;
                this.calculateTotal();
            },

            getAmount(amount) {
                if (
                    this.steps[this.data.stepIndex].rows[this.data.id] &&
                    this.steps[this.data.stepIndex].rows[this.data.id].amount
                ) {
                    this.amount = this.steps[this.data.stepIndex].rows[
                        this.data.id
                    ].amount;
                }
            },

            getPeriod(period) {
                if (
                    this.steps[this.data.stepIndex].rows[this.data.id] &&
                    this.steps[this.data.stepIndex].rows[this.data.id].period
                ) {
                    this.period = this.steps[this.data.stepIndex].rows[
                        this.data.id
                    ].period;
                }
            },

            calculateTotal() {
                const frequency =
                    typeof this.spendFrequency === "undefined" ||
                    isNaN(this.spendFrequency) ||
                    !this.spendFrequency
                        ? 0
                        : parseFloat(this.spendFrequency);

                const amount =
                    typeof this.amount === "undefined" ||
                    isNaN(this.amount) ||
                    !this.amount
                        ? 0
                        : parseFloat(this.amount);

                const total = frequency * parseInt(this.period, 10) * amount;
                const result = isNaN(total) ? 0 : Math.abs(total);

                this.updateRow({
                    stepIndex: this.data.stepIndex,
                    rowIndex: this.data.id,
                    title: this.data.title,
                    spendFrequency: frequency,
                    period: parseInt(this.period, 10),
                    amount: amount,
                    total
                });

                this.total = formatCurrency(result);
            },

            sanitizeCurrencyValue(value) {
                value = value.toString();
                value = value.replace(/[^.\d]/g, "");
                value = value.replace(/\.+/g, ".");
                value = value.replace(/^([^.]+\.[^.]{1,2}).*$/g, "$1");
                return value;
            },

            sanitizeIntegerValue(value) {
                return value.toString().replace(/[^0-9]/g, "");
            }
        },

        created() {
            this.getAmount();
            this.getFrequency();
            this.getPeriod();
        }
    };
</script>

<style lang="scss">
    .c-row {
        display: flex;
        justify-content: space-around;
        padding: 0.9375rem 2.75rem 2.0625rem 0;

        &-title {
            background-color: #eef8fb;
            min-width: 24%;
            margin-top: auto;
            margin-right: 2.5rem;
            font-family: "Roboto", sans-serif;
            font-weight: 500;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            position: relative;
            letter-spacing: 0.01em;
            flex-wrap: wrap;
            flex-flow: column;
            align-items: flex-end;
            padding: 0.8125rem 2.5rem 0.3125rem 0;
            text-align: right;

            &::before {
                content: "";
                position: absolute;
                width: 500%;
                top: 0;
                bottom: 0;
                right: 100%;
                background-color: #eef8fb;
            }

            &::after {
                content: "";
                position: absolute;
                display: inline-block;
                top: 0.1875rem;
                right: 0;
            }

            &__info {
                display: none;
            }

            &__info-icon {
                position: absolute;
                top: 0.625rem;
                right: 1.3125rem;

                &:hover {
                    ~ div.c-row-title__info {
                        display: block;
                        width: 18.75rem;
                        background-color: #f1f0f0;
                        position: absolute;
                        top: -120%;
                        right: -110%;
                        z-index: 15;
                        font-size: 0.8rem;
                        border: 1px solid black;
                        border-radius: 0.3125rem;
                        padding: 1rem;
                        text-align: left;

                        p {
                            margin: 0;
                            padding: 0;
                        }
                    }
                }
            }

            span {
                font-size: 0.75rem;
                text-align: right;
            }
        }

        @media screen and (max-width: 1024px) {
            flex-flow: row wrap;
            padding-bottom: 1.25rem;
            padding-right: 0;

            &-title {
                justify-content: flex-end;
                margin-bottom: 0.9375rem;
                font-size: 1.2rem;
                padding-right: 2.8125rem;
                width: 40%;
                font-size: 1rem;

                &__info-icon {
                    &:hover,
                    &:focus {
                        ~ div.c-row-title__info {
                            top: 80%;
                            right: 0%;
                            width: 15rem;
                        }
                    }
                }
            }
        }

        @media screen and (max-width: 767px) {
            &-title {
                margin-right: 0;
                width: 55%;

                &__info-icon {
                    &:hover,
                    &:focus {
                        ~ div.c-row-title__info {
                            top: 70%;
                            right: -10%;
                        }
                    }
                }
            }
        }
    }
</style>
